import { useCallback, useRef, useState, type ReactNode } from "react";
import {
  type CreateSlideConfig,
  type DesignMode,
  type SlideSource,
  type Tone,
  SLIDE_COUNT_MAX,
  SLIDE_COUNT_MIN,
  TONES,
} from "../hooks/types";
import styles from "./CreateSlide.module.css";

const TOTAL_STEPS = 4;
/** Backend requires topic >= 3 chars for topic input. */
const TOPIC_MIN = 3;

// One focused decision per step, so no single step feels crammed.
const STEP_COPY: Record<number, { title: string; sub: string }> = {
  1: { title: "How would you like to start?", sub: "Pick a source. We'll use it to generate your slide outline." },
  2: { title: "Tell us about your deck", sub: "A few details so the AI nails the right tone and length." },
  3: { title: "How should we design the slides?", sub: "AI design is fully custom; templates ground each slide in a reviewed layout." },
  4: { title: "Ready to generate?", sub: "Review your choices, then generate the deck." },
};

const TEMPLATE_REVIEW_COPY = {
  title: "Use slide templates",
  sub: "Pick from our reviewed layout library — coming soon.",
};

// Fallback so the copy lookup stays total under noUncheckedIndexedAccess.
const EMPTY_COPY = { title: "", sub: "" };

function getDotClass(n: number, step: number): string {
  if (n === step) return `${styles.dot} ${styles.dotActive}`;
  if (n < step) return `${styles.dot} ${styles.dotDone}`;
  return styles.dot ?? "";
}

function getDetailIssues(config: CreateSlideConfig): string[] {
  const issues: string[] = [];

  if (config.source === "text" && config.topic.trim().length < TOPIC_MIN) {
    issues.push(`Topic (at least ${TOPIC_MIN} characters)`);
  }

  if (config.source === "upload" && config.documents.length === 0) {
    issues.push("A document to upload");
  }

  if (!config.audience.trim()) {
    issues.push("Audience");
  }

  if (config.slideCount < SLIDE_COUNT_MIN) {
    issues.push(`At least ${SLIDE_COUNT_MIN} slides`);
  }

  return issues;
}

interface CreateSlideWizardProps {
  readonly config: CreateSlideConfig;
  readonly onChange: (patch: Partial<CreateSlideConfig>) => void;
  readonly onGenerate: () => void;
}

export function CreateSlideWizard({ config, onChange, onGenerate }: CreateSlideWizardProps) {
  const [step, setStep] = useState(1);

  const goNext = useCallback(() => setStep((s) => Math.min(TOTAL_STEPS, s + 1)), []);
  const goBack = useCallback(() => setStep((s) => Math.max(1, s - 1)), []);

  // Required-field validation for the details step (step 2), surfaced as a
  // visible list so the user knows why "Next" is disabled (rather than a
  // silently-greyed button).
  const detailIssues = step === 2 ? getDetailIssues(config) : [];
  const canAdvance = step !== 2 || detailIssues.length === 0;

  const isTemplatesReview = step === 4 && config.designMode === "templates";
  const copy = isTemplatesReview ? TEMPLATE_REVIEW_COPY : (STEP_COPY[step] ?? EMPTY_COPY);

  return (
    <div className={styles.wrap}>
      <div className={styles.progressDots} aria-hidden="true">
        {Array.from({ length: TOTAL_STEPS }, (_, i) => {
          const n = i + 1;
          return <span key={n} className={getDotClass(n, step)} />;
        })}
      </div>

      <div className={styles.stepHead}>
        <h3 className={styles.stepTitle}>{copy.title}</h3>
        <p className={styles.stepSub}>{copy.sub}</p>
      </div>

      <div className={styles.stepBody}>
        {step === 1 && <StepSource config={config} onChange={onChange} />}
        {step === 2 && <StepDetails config={config} onChange={onChange} />}
        {step === 3 && <StepDesign config={config} onChange={onChange} />}
        {step === 4 && (isTemplatesReview ? <TemplatesPlaceholder /> : <StepReview config={config} />)}

        {step === 2 && detailIssues.length > 0 && (
          <output className={styles.validationHint}>
            Required to continue: {detailIssues.join(", ")}.
          </output>
        )}
      </div>

      <div className={styles.footer}>
        <div className={styles.footerLeft}>
          {step > 1 && (
            <button type="button" className={styles.navBtn} onClick={goBack}>
              ← Back
            </button>
          )}
          <span className={styles.footerStep}>
            Step {step} of {TOTAL_STEPS}
          </span>
        </div>
        <FooterAction
          step={step}
          isTemplatesReview={isTemplatesReview}
          canAdvance={canAdvance}
          goNext={goNext}
          onGenerate={onGenerate}
        />
      </div>
    </div>
  );
}

function FooterAction({
  step,
  isTemplatesReview,
  canAdvance,
  goNext,
  onGenerate,
}: {
  readonly step: number;
  readonly isTemplatesReview: boolean;
  readonly canAdvance: boolean;
  readonly goNext: () => void;
  readonly onGenerate: () => void;
}) {
  if (step < TOTAL_STEPS) {
    return (
      <button type="button" className={styles.navPrimary} onClick={goNext} disabled={!canAdvance}>
        Next →
      </button>
    );
  }
  if (isTemplatesReview) {
    return (
      <button type="button" className={styles.navPrimary} disabled>
        Coming soon
      </button>
    );
  }
  return (
    <button type="button" className={styles.navPrimary} onClick={onGenerate}>
      ✨ Generate
    </button>
  );
}

/* ── Step 1: source ─────────────────────────────── */

interface StepProps {
  readonly config: CreateSlideConfig;
  readonly onChange: (patch: Partial<CreateSlideConfig>) => void;
}

// Thin line icons (pencil / upload tray) matching the approved mockup — they
// inherit `currentColor` from .choiceIcon so they tint with the card.
const ICON_PENCIL = (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 13.5V16h2.5l7.4-7.4-2.5-2.5L4 13.5z" />
    <path d="M13.4 5.6l1-1a.7.7 0 011 0l1 1a.7.7 0 010 1l-1 1-2.5-2.5z" />
  </svg>
);
const ICON_UPLOAD = (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 3v9" />
    <path d="M6.5 6.5 10 3l3.5 3.5" />
    <path d="M4 13v2.5A1.5 1.5 0 005.5 17h9a1.5 1.5 0 001.5-1.5V13" />
  </svg>
);

const SOURCES: ReadonlyArray<{ id: SlideSource; icon: ReactNode; title: string; desc: string }> = [
  { id: "text", icon: ICON_PENCIL, title: "Text prompt", desc: "Describe a topic or write your own outline" },
  { id: "upload", icon: ICON_UPLOAD, title: "Upload document(s)", desc: "PDF, Word, or text files we'll summarise" },
];

function StepSource({ config, onChange }: StepProps) {
  return (
    <div className={styles.choiceGrid}>
      {SOURCES.map((s) => (
        <button
          key={s.id}
          type="button"
          className={`${styles.choiceCard} ${config.source === s.id ? styles.choiceCardActive : ""}`}
          onClick={() => onChange({ source: s.id })}
          aria-pressed={config.source === s.id}
        >
          <span className={styles.choiceIcon} aria-hidden="true">{s.icon}</span>
          <span className={styles.choiceTitle}>{s.title}</span>
          <span className={styles.choiceDesc}>{s.desc}</span>
        </button>
      ))}
    </div>
  );
}

/* ── Step 2: details ────────────────────────────── */

async function readChars(file: File): Promise<number> {
  try {
    const text = await file.text();
    return text.length;
  } catch {
    return file.size;
  }
}

// Backend-supported document types for server-side extraction.
const ACCEPTED_EXT = [".txt", ".md", ".pdf", ".docx", ".doc"];

function isAcceptedFile(name: string): boolean {
  const lower = name.toLowerCase();
  return ACCEPTED_EXT.some((ext) => lower.endsWith(ext));
}

function clampSlideCount(raw: string): number {
  const n = Number(raw);
  const safe = Number.isNaN(n) ? SLIDE_COUNT_MIN : n;
  return Math.min(SLIDE_COUNT_MAX, Math.max(SLIDE_COUNT_MIN, safe));
}

function DocumentUploader({ config, onChange }: StepProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const addFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const all = Array.from(files);
      const accepted = all.filter((f) => isAcceptedFile(f.name));
      const rejected = all.filter((f) => !isAcceptedFile(f.name));
      setFileError(
        rejected.length > 0
          ? `Unsupported file type: ${rejected.map((f) => f.name).join(", ")}. Use PDF, Word (.doc/.docx), or text (.txt/.md).`
          : null,
      );
      if (accepted.length === 0) return;
      // De-dupe by name — both against already-added docs and within this batch.
      // Documents are keyed/removed by name, so duplicates would collide on the
      // React key and make one remove-click delete both entries.
      const existing = new Set(config.documents.map((d) => d.name));
      const seen = new Set<string>();
      const fresh = accepted.filter((f) => {
        if (existing.has(f.name) || seen.has(f.name)) return false;
        seen.add(f.name);
        return true;
      });
      if (fresh.length === 0) return;
      const docs = await Promise.all(
        fresh.map(async (f) => ({ name: f.name, chars: await readChars(f), file: f })),
      );
      onChange({ documents: [...config.documents, ...docs] });
    },
    [config.documents, onChange],
  );

  const removeDoc = useCallback(
    (name: string) => {
      onChange({ documents: config.documents.filter((d) => d.name !== name) });
    },
    [config.documents, onChange],
  );

  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>
        Documents <span className={styles.req}>*</span>
      </span>
      <button
        type="button"
        className={`${styles.drop} ${dragging ? styles.dropActive : ""}`}
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          addFiles(e.dataTransfer.files).catch(() => undefined);
        }}
      >
        <span className={styles.dropIcon} aria-hidden="true">📎</span>
        <span>Drop documents here or click to browse</span>
        <span className={styles.dropHint}>PDF, Word, or text · up to ~5 pages</span>
      </button>
      <input
        ref={fileRef}
        type="file"
        multiple
        accept=".txt,.md,.pdf,.doc,.docx,text/*"
        style={{ display: "none" }}
        onChange={(e) => {
          addFiles(e.target.files).catch(() => undefined);
          e.target.value = "";
        }}
      />
      {fileError && <p className={styles.error}>{fileError}</p>}
      {config.documents.length > 0 && (
        <div className={styles.fileChips}>
          {config.documents.map((d) => (
            <span key={d.name} className={styles.fileChip}>
              {d.name} · {formatChars(d.chars)}
              <button
                type="button"
                className={styles.fileChipRemove}
                onClick={() => removeDoc(d.name)}
                aria-label={`Remove ${d.name}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function StepDetails({ config, onChange }: StepProps) {
  const isUpload = config.source === "upload";

  return (
    <>
      {/* Upload source → document uploader; text source → no upload UI. */}
      {isUpload && <DocumentUploader config={config} onChange={onChange} />}

      <div className={styles.field}>
        <span className={styles.fieldLabel}>
          Topic / context{!isUpload && <span className={styles.req}> *</span>}
        </span>
        <textarea
          className={styles.textArea}
          value={config.topic}
          onChange={(e) => onChange({ topic: e.target.value })}
          placeholder={
            isUpload
              ? "Optional — extra context to steer the summary"
              : "What's this deck about?"
          }
        />
      </div>

      {/* Audience + slide count share a row; tone spans its own full-width row
          below — matches the mockup's step-2 layout. */}
      <div className={styles.fieldRow}>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>
            Audience <span className={styles.req}>*</span>
          </span>
          <input
            className={styles.input}
            value={config.audience}
            onChange={(e) => onChange({ audience: e.target.value })}
            placeholder="e.g. Stakeholders"
          />
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Slide count</span>
          <input
            type="number"
            className={styles.input}
            min={SLIDE_COUNT_MIN}
            max={SLIDE_COUNT_MAX}
            value={config.slideCount}
            onChange={(e) => onChange({ slideCount: clampSlideCount(e.target.value) })}
          />
        </div>
      </div>

      <div className={styles.field}>
        <span className={styles.fieldLabel}>Tone</span>
        <select
          className={styles.select}
          value={config.tone}
          onChange={(e) => onChange({ tone: e.target.value as Tone })}
          aria-label="Tone"
        >
          {TONES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.field}>
        <span className={styles.fieldLabel}>Bookend slides</span>
        <div className={styles.bookendRow}>
          <BookendToggle
            checked={config.coverSlide}
            title="Cover slide"
            desc="Title slide opens the deck"
            onToggle={() => onChange({ coverSlide: !config.coverSlide })}
          />
          <BookendToggle
            checked={config.closingSlide}
            title="Closing slide"
            desc="Wrap-up at the end"
            onToggle={() => onChange({ closingSlide: !config.closingSlide })}
          />
        </div>
      </div>
    </>
  );
}

function BookendToggle({
  checked,
  title,
  desc,
  onToggle,
}: {
  readonly checked: boolean;
  readonly title: string;
  readonly desc: string;
  readonly onToggle: () => void;
}) {
  return (
    <label className={`${styles.toggleCard} ${checked ? styles.toggleCardActive : ""}`} aria-label={title}>
      <input type="checkbox" checked={checked} onChange={onToggle} />
      <span className={styles.toggleText}>
        <span className={styles.toggleTitle}>{title}</span>
        <span className={styles.toggleDesc}>{desc}</span>
      </span>
    </label>
  );
}

function formatChars(chars: number): string {
  return chars >= 1000 ? `${(chars / 1000).toFixed(1)}k chars` : `${chars} chars`;
}

/* ── Step 3: design mode ────────────────────────── */

const ICON_SPARKLE = (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 2.5l1.4 4.6a2 2 0 001.5 1.5L17.5 10l-4.6 1.4a2 2 0 00-1.5 1.5L10 17.5l-1.4-4.6a2 2 0 00-1.5-1.5L2.5 10l4.6-1.4a2 2 0 001.5-1.5z" />
  </svg>
);
const ICON_FOLDER = (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2.75 6.25A1.25 1.25 0 014 5h3.3a1 1 0 01.7.3l1.2 1.2H16a1.25 1.25 0 011.25 1.25v6A1.25 1.25 0 0116 15H4a1.25 1.25 0 01-1.25-1.25z" />
  </svg>
);

const DESIGN_MODES: ReadonlyArray<{ id: DesignMode; icon: ReactNode; title: string; desc: string; badge?: string }> = [
  { id: "freehand", icon: ICON_SPARKLE, title: "AI design (freehand)", desc: "AI builds each slide from scratch — bespoke layouts, no constraints" },
  { id: "templates", icon: ICON_FOLDER, title: "Use slide templates", desc: "Pick from our reviewed layout library; AI fills in your content", badge: "Coming soon" },
];

function StepDesign({ config, onChange }: StepProps) {
  return (
    <div className={styles.choiceGrid}>
      {DESIGN_MODES.map((m) => (
        <button
          key={m.id}
          type="button"
          className={`${styles.choiceCard} ${config.designMode === m.id ? styles.choiceCardActive : ""}`}
          onClick={() => onChange({ designMode: m.id })}
          aria-pressed={config.designMode === m.id}
        >
          <span className={styles.choiceIcon} aria-hidden="true">{m.icon}</span>
          <span className={styles.choiceTitle}>
            {m.title}
            {m.badge && <span className={styles.comingSoonBadge}>{m.badge}</span>}
          </span>
          <span className={styles.choiceDesc}>{m.desc}</span>
        </button>
      ))}
    </div>
  );
}

/* ── Step 4 (templates): placeholder — backend WIP ── */

function TemplatesPlaceholder() {
  return (
    <div className={styles.placeholder}>
      <span className={styles.placeholderIcon} aria-hidden="true">🗂</span>
      <span className={styles.placeholderTitle}>Slide templates are coming soon</span>
      <span className={styles.placeholderText}>
        The reviewed-layout library is still being built by the backend team. For now, go
        back and choose <strong>AI design (freehand)</strong> to generate your deck.
      </span>
    </div>
  );
}

/* ── Step 4: review ─────────────────────────────── */

function StepReview({ config }: { readonly config: CreateSlideConfig }) {
  const title = config.topic.trim() || config.documents[0]?.name || "Your deck";
  const designLabel = config.designMode === "templates" ? "slide templates" : "AI freehand";
  const bookends = [config.coverSlide && "cover", config.closingSlide && "closing"]
    .filter(Boolean)
    .join(" + ");
  const meta = [
    `${config.slideCount} slides`,
    designLabel,
    `${config.tone.toLowerCase()} tone`,
    bookends || null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className={styles.reviewWrap}>
      <div className={styles.reviewCard}>
        <span className={styles.reviewTitle}>{title}</span>
        <span className={styles.reviewMeta}>{meta}</span>
      </div>
      <p className={styles.reviewNote}>
        Generation streams per-slide progress into the <strong>CHAT</strong> tab — PowerPoint
        stays fully usable.
      </p>
    </div>
  );
}
